import React from "react";
import { useNavigation, useTranslate } from "@refinedev/core";
import { DateField } from "@refinedev/mui";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import type { UnifiedEvent } from "../../../types/events";
import { EVENT_COLORS, ALERT_STATUS_COLORS } from "../../../constants/eventColors";
import {
  isAlertEvent,
  isJourneyEvent,
  isForbiddenZoneEvent,
} from "../../../utils/unifiedEvents";

interface UnifiedEventCardProps {
  event: UnifiedEvent;
  isFocused?: boolean;
  isAnyFocused?: boolean;
  onFocusToggle?: () => void;
  onNavigate?: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
  cardWidth: string;
}

/** Solid status pill — GitHub/Linear style — high contrast on any background */
function StatusChip({ event, translate }: { event: UnifiedEvent; translate: any }) {
  let label = "";
  let bgColor = "";

  if (isAlertEvent(event)) {
    bgColor = ALERT_STATUS_COLORS[event.data.status];
    label = translate(`monitoring.status.${event.data.status.toLowerCase()}`, event.data.status);
  } else {
    return null;
  }

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: bgColor,
        color: "#fff",
        fontWeight: 700,
        fontSize: "0.65rem",
        height: "22px",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

export const UnifiedEventCard: React.FC<UnifiedEventCardProps> = ({
  event,
  isFocused,
  isAnyFocused,
  onFocusToggle,
  onNavigate,
  onHoverStart,
  onHoverEnd,
  cardWidth,
}) => {
  const translate = useTranslate();
  const { show } = useNavigation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const colors = EVENT_COLORS[event.eventType];
  const isDark = theme.palette.mode === "dark";

  const handleClick = () => {
    if (isAlertEvent(event)) {
      if (event.data.isBroadcasting) {
        show("monitoring", event.data.id);
      } else {
        window.open(`/history/alert/${event.data.id}`, "_blank");
      }
    } else if (isJourneyEvent(event)) {
      window.open(`/history/journey/${event.data.id}`, "_blank");
    } else if (isForbiddenZoneEvent(event)) {
      window.open(`/history/forbidden-zone/${event.data.id}`, "_blank");
    }
  };

  const typeLabel =
    event.eventType === "alert"
      ? translate("monitoring.alert", "Alert")
      : event.eventType === "journey"
        ? translate("monitoring.journey", "Journey")
        : translate("monitoring.forbiddenZoneFull", "Zone interdite");

  return (
    <Card
      sx={{
        minWidth: cardWidth,
        maxWidth: cardWidth,
        flex: "0 0 auto",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        border: `2px solid ${colors.main}`,
        borderRadius: "12px",
        backgroundColor: isDark ? alpha(colors.main, 0.08) : alpha(colors.main, 0.04),
        boxShadow: isFocused
          ? `0 4px 16px ${alpha(colors.main, 0.35)}`
          : `0 1px 4px ${alpha("#000", 0.08)}`,
        opacity: isAnyFocused && !isFocused ? 0.65 : 1,
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: isDark ? alpha(colors.main, 0.16) : alpha(colors.main, 0.1),
        },
      }}
      onClick={onFocusToggle}
      onDoubleClick={onNavigate}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2 }, pb: { xs: 3, md: 3.5 }, "&:last-child": { pb: { xs: 3, md: 3.5 } } }}>
        {/* Header: user name + status chip top-right */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.5, mb: 1 }}>
          <Typography
            variant={isSmallScreen ? "body2" : "subtitle2"}
            color="text.primary"
            sx={{ fontWeight: 700, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
          >
            {event.userName || event.data?.user?.email || "-"}
          </Typography>
          <StatusChip event={event} translate={translate} />
        </Box>

        {/* Type-specific details */}
        {isAlertEvent(event) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            <DateField value={event.timestamp} format="DD/MM/YYYY HH:mm" />
          </Typography>
        )}

        {isJourneyEvent(event) && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              <DateField value={event.timestamp} format="DD/MM/YYYY HH:mm" />
            </Typography>
            {event.data.lastHeartbeat && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                {translate("monitoring.lastUpdate", "Last update")}: <DateField value={event.data.lastHeartbeat} format="HH:mm" />
              </Typography>
            )}
          </Box>
        )}

        {isForbiddenZoneEvent(event) && (
          <Box>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500, mb: 0.5 }}>
              {event.data.place?.emoji} {event.data.place?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              <DateField value={event.timestamp} format="DD/MM/YYYY HH:mm" />
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Type badge — fused bottom-left */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          backgroundColor: colors.main,
          color: "#fff",
          fontWeight: 700,
          fontSize: "0.65rem",
          lineHeight: 1,
          px: 1.2,
          pt: "6px",
          pb: "4px",
          borderTopRightRadius: "10px",
          letterSpacing: "0.3px",
          textTransform: "uppercase",
        }}
      >
        {typeLabel}
      </Box>

      {/* Live/En cours badge — fused bottom-right */}
      {event.isActive && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: colors.main,
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.65rem",
            lineHeight: 1,
            px: 1.2,
            pt: "6px",
            pb: "4px",
            borderTopLeftRadius: "10px",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          {isAlertEvent(event) && (
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: "#fff",
                flexShrink: 0,
                animation: "livePulse 1.5s ease-in-out infinite",
                "@keyframes livePulse": {
                  "0%, 100%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.4, transform: "scale(0.75)" },
                },
              }}
            />
          )}
          {isAlertEvent(event)
            ? translate("monitoring.live", "Live")
            : translate("monitoring.inProgress", "En cours")}
        </Box>
      )}
    </Card>
  );
};
